<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  CogIcon,
  AdjustmentsIcon,
  LogoutIcon,
  UserIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/vue/outline";
import { useAuthStore } from "@/stores";
import { computed, inject } from "vue";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useAuth } from "@websanova/vue-auth";
import SearchUserDialog from "@/components/layout/SearchUserDialog.vue";
import SwitchUserDialog from "@/components/layout/SwitchUserDialog.vue";

const axios = inject("axios");
const auth = useAuth();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const switchDialogOpen = ref(false);

const profilePicture = computed(() => authStore.user.profile_picture);
const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isAccountManager = computed(() => authStore.isAccountManager);

const initials = computed(() => {
  let initials = user.value.name.split(" ");

  if (initials.length > 1) {
    initials = initials.shift().charAt(0) + initials.pop().charAt(0);
  } else {
    initials = user.value.name.substring(0, 2);
  }

  return initials.toUpperCase();
});

const redirect = (url) => {
  window.open(url, "_blank");
};

const logout = () => {
  axios.get("/api/okta/logout").then(({ data }) => {
    auth.logout();
    localStorage.removeItem("nextUrl");
    window.location.href = data;
  });
}
</script>

<template>
  <Menu as="div" class="relative ml-3">
    <div>
      <MenuButton
        class="relative flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
      >
        <span class="absolute -inset-1.5" />
        <span class="sr-only">Open user menu</span>
        <span
          class="font-medium text-gray-600 dark:text-gray-300"
          v-if="!profilePicture"
          >{{ initials }}</span
        >
        <img
          class="w-8 h-8 rounded-full object-cover object-center"
          v-if="profilePicture"
          :src="profilePicture"
          alt=""
        />
      </MenuButton>
    </div>
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-brand py-1 shadow-lg ring-1 ring-white focus:outline-hidden"
      >
        <MenuItem v-slot="{ active }">
          <a
            @click="redirect('/central/profile')"
            class="block px-4 py-2 text-sm hover:text-white"
            :class="[
              {
                'bg-kintz-primary-1 text-kintz-primary-2': active,
                'bg-kintz-primary-3 text-white': !active,
              },
            ]"
          >
            <UserIcon class="mr-2 h-5 w-5 inline" />Profile
          </a>
        </MenuItem>
        <MenuItem v-slot="{ active }">
          <a
            href="https://docs.revdojo.com/"
            target="_blank"
            class="block px-4 py-2 text-sm hover:text-white"
            :class="[
              {
                'bg-kintz-primary-1 text-kintz-primary-2': active,
                'bg-kintz-primary-3 text-white': !active,
              },
            ]"
          >
            <QuestionMarkCircleIcon class="mr-2 h-5 w-5 inline" />FAQ
          </a>
        </MenuItem>
        <MenuItem v-if="isSuperAdmin" v-slot="{ active }">
          <a
            @click="redirect('/central/preference')"
            class="block px-4 py-2 text-sm hover:text-white"
            :class="[
              {
                'bg-kintz-primary-1 text-kintz-primary-2': active,
                'bg-kintz-primary-3 text-white': !active,
              },
            ]"
          >
            <CogIcon class="mr-2 h-5 w-5 inline" />Preference
          </a>
        </MenuItem>
        <MenuItem v-slot="{ active }" v-if="isSuperAdmin || isAccountManager">
          <router-link
            :to="{ name: 'LMS Settings Page' }"
            class="block px-4 py-2 text-sm hover:text-white"
            :class="[
              {
                'bg-kintz-primary-1 text-kintz-primary-2': active,
                'bg-kintz-primary-3 text-white': !active,
              },
            ]"
          >
            <AdjustmentsIcon class="mr-2 h-5 w-5 inline" />Settings
          </router-link>
        </MenuItem>
        <MenuItem v-slot="{ active }">
          <SearchUserDialog
            v-model="switchDialogOpen"
            :active="active"
            kintz-app
          />
        </MenuItem>
        <MenuItem v-slot="{ active }">
          <button
            @click="logout()"
            type="button"
            class="px-4 py-2 text-sm w-full flex justify-start items-center hover:text-white"
            :class="[
              {
                'bg-kintz-primary-1 text-kintz-primary-2': active,
                'bg-kintz-primary-3 text-white': !active,
              },
            ]"
          >
            <LogoutIcon class="mr-2 h-5 w-5 inline" />Sign out
          </button>
        </MenuItem>
      </MenuItems>
    </transition>

    <SwitchUserDialog v-model="switchDialogOpen" v-if="switchDialogOpen" />
  </Menu>
</template>

<style lang="scss" scoped></style>
